import { isEmpty, merge } from 'lodash-es';

import type { TruncationMode } from '@dynatrace/strato-components/typography';

import { interactionsFor } from './axis-interactions.js';
import { doesNothing } from './defaults.js';
import { isXAxis } from './is-axis.js';
import { TRUNCATION_MODE } from '../../core/constants.js';
import { isCustomColorPalette } from '../../core/types/color-palette.js';
import { isColoredRangeArray } from '../../core/types/colored-range.js';
import { isValidColorPalette } from '../../core/utils/is-valid-color-palette.js';
import { isValidLegendPosition } from '../../core/utils/is-valid-legend-position.js';
import { isValidToolbarPlacement } from '../../core/utils/is-valid-toolbar-placement.js';
import { buildThresholds } from '../components/thresholds/builder.js';
import {
  DEFAULT_AXIS_CONFIG,
  DEFAULT_COLOR_PALETTE,
  defaultConfig,
  defaultLegend,
  defaultToolbar,
} from '../constants/defaults.js';
import { XYChartAnnotationsConfigProps } from '../context/XYChartAnnotationsConfig.context.js';
import { XYAxisMapper } from '../types/axis.js';
import {
  DOT_VARIANT,
  type ExportableXYChartProps,
  RECT_VARIANT,
  type XYChartAxisInternal,
  type XYChartDotSeriesInternal,
  type XYChartInternals,
  type XYChartRectSeriesInternal,
  type XYChartSlots,
} from '../types/xy-chart-internals.js';
import type {
  XYChartAxisProps,
  XYChartChartLegendProps,
  XYChartColorPalette,
  XYChartConfigProperties,
  XYChartSeriesBaseProps,
  XYChartToolbarProps,
} from '../types/xy-chart.js';

/**
 * Builds a legend configuration based on context and slots
 * @param slotsLegend - legend slots
 * @param contextLegend - legend context
 */
function getLegendConfiguration(
  slotsLegend: XYChartChartLegendProps,
  contextLegend: XYChartChartLegendProps | undefined,
): XYChartChartLegendProps {
  const legendConfig: XYChartChartLegendProps = merge(
    {},
    defaultLegend,
    contextLegend,
    slotsLegend,
  );

  legendConfig.position = isValidLegendPosition(legendConfig.position)
    ? legendConfig.position
    : defaultLegend.position;

  return legendConfig;
}

function getToolbarConfiguration(
  slotsToolbar: XYChartToolbarProps,
  contextToolbar: XYChartToolbarProps | undefined,
) {
  const toolbarConfig = merge({}, defaultToolbar, contextToolbar, slotsToolbar);

  toolbarConfig.placement = isValidToolbarPlacement(toolbarConfig.placement)
    ? toolbarConfig.placement
    : defaultToolbar.placement;

  return toolbarConfig;
}

function getAxesWithSeries(
  axisSlots: XYChartAxisInternal[],
  series: XYChartSeriesBaseProps[],
) {
  let axesConfig: XYChartAxisInternal[] = [];
  const matchingIds = new Set();

  axisSlots.forEach((axis) => {
    const isAlreadyDefined = matchingIds.has(axis.id);

    if (isAlreadyDefined) {
      return;
    }

    const hasMatchingSeriesId = series.find((series) => {
      return isXAxis(axis)
        ? series.xAxisId === axis.id
        : series.yAxisId === axis.id;
    });

    if (hasMatchingSeriesId) {
      matchingIds.add(axis.id);
      axesConfig = [...axesConfig, axis];
    }
  });

  return axesConfig;
}

/**
 * Builds a ColorPalette configuration based on context and props
 * @param contextColorPalette - color palette context
 * @param propsColorPalette - color palette props
 */
function getColorPaletteConfiguration(
  contextColorPalette?: XYChartColorPalette,
  propsColorPalette?: XYChartColorPalette,
) {
  const colorPalette =
    propsColorPalette ?? contextColorPalette ?? DEFAULT_COLOR_PALETTE;

  return isColoredRangeArray(colorPalette) ||
    isCustomColorPalette(colorPalette) ||
    isValidColorPalette(colorPalette)
    ? colorPalette
    : DEFAULT_COLOR_PALETTE;
}

export function getTruncationModeConfiguration(
  contextTruncationMode?: TruncationMode,
  propsTruncationMode?: TruncationMode,
) {
  return propsTruncationMode ?? contextTruncationMode ?? TRUNCATION_MODE.Middle;
}

export function getXYChartAnnotationsConfiguration(
  slotsAnnotations?: XYChartAnnotationsConfigProps,
  configAnnotations?: XYChartAnnotationsConfigProps,
) {
  if (isEmpty(slotsAnnotations) && !configAnnotations) {
    return undefined;
  }
  return merge({}, configAnnotations, slotsAnnotations);
}

/**
 * Creates a XY chart configuration merging the configuration
 * provided in the context config, slots, and defaults.
 * @param slots - xy chart slots
 * @param contextConfig - config pass in config context
 * @param rootProps - Config props passed in root component level
 */
export const createXYChartConfiguration = (
  slots: XYChartSlots,
  contextConfig: XYChartConfigProperties,
  rootProps: ExportableXYChartProps,
): Omit<Required<XYChartConfigProperties>, 'annotations'> & {
  annotations?: XYChartAnnotationsConfigProps;
} => {
  const colorPalette = getColorPaletteConfiguration(
    contextConfig.colorPalette,
    rootProps?.colorPalette,
  );

  const truncationMode = getTruncationModeConfiguration(
    contextConfig.truncationMode,
    rootProps?.truncationMode,
  );

  const legend = getLegendConfiguration(slots.legend, contextConfig.legend);
  const xAxes = getAxes(slots.xAxes, contextConfig.xAxes);
  const yAxes = getAxes(slots.yAxes, contextConfig.yAxes);
  const toolbar = getToolbarConfiguration(slots.toolbar, contextConfig.toolbar);
  const annotations = getXYChartAnnotationsConfiguration(
    slots.annotations,
    contextConfig.annotations,
  );

  const thresholds = [...slots.thresholds, ...(contextConfig.thresholds || [])];
  return {
    colorPalette,
    legend,
    toolbar,
    truncationMode,
    xAxes,
    yAxes,
    thresholds,
    annotations,
  };
};

export function getAxes<T extends XYChartAxisProps>(
  slotAxes: T[],
  contextAxes: T[] | undefined,
): T[] {
  const configContextAxes = contextAxes ?? [];
  const ids = new Set<string>();
  const axesConfig = [...slotAxes, ...configContextAxes];

  return axesConfig.reduce<T[]>((acc: T[], axis: T) => {
    if (!ids.has(axis.id)) {
      acc.push({ ...DEFAULT_AXIS_CONFIG, ...axis });
      ids.add(axis.id);
    }

    return acc;
  }, []);
}

export const deserializeConfig = (
  serializedConfig: string,
): XYChartConfigProperties => {
  try {
    return merge({}, defaultConfig, JSON.parse(serializedConfig));
  } catch {
    return defaultConfig;
  }
};

type InternalConfig = {
  xAxes: XYChartSlots['xAxes'];
  yAxes: XYChartSlots['yAxes'];
  thresholds: XYChartSlots['thresholds'];
};
type InternalSlots = {
  rectSeries: XYChartSlots['rectSeries'];
  dotSeries: XYChartSlots['dotSeries'];
};
type BuildInternalConfig = (
  config: InternalConfig,
  slots: InternalSlots,
) => XYChartInternals;

export const buildInternalConfig: BuildInternalConfig = (config, slots) => {
  const rectSeries: XYChartRectSeriesInternal[] = slots.rectSeries.map(
    (series) => ({
      ...series,
      id: crypto.randomUUID(),
      type: RECT_VARIANT,
    }),
  );

  const dotSeries: XYChartDotSeriesInternal[] = slots.dotSeries.map(
    (series) => ({
      ...series,
      id: crypto.randomUUID(),
      type: DOT_VARIANT,
    }),
  );

  const yAxes = config.yAxes.map((axis) => ({
    ...axis,
    coordinate: 'y',
    tickLabelLayout: 'horizontal',
    type: XYAxisMapper[axis.type],
    interactions: { zoom: false, pan: false, infinite: false },
    onZoomChange: doesNothing,
  }));
  const xAxes = config.xAxes.map((axis) => ({
    ...axis,
    coordinate: 'x' as const,
    tickLabelLayout: axis.tickLabelLayout ?? 'horizontal',
    type: XYAxisMapper[axis.type],
    interactions: interactionsFor(axis),
    onZoomChange: axis.onZoomChange ?? doesNothing,
  }));

  const axes = getAxesWithSeries(
    [...yAxes, ...xAxes] as XYChartAxisInternal[],
    [...rectSeries, ...dotSeries],
  );

  const thresholds = buildThresholds(yAxes, xAxes, config.thresholds);

  return { rectSeries, dotSeries, axes, thresholds };
};
